import { useCallback, useRef, useEffect } from 'react';

/**
 * useClearableTimeout
 *
 * Returns a function to start a timeout and a function to clear it. The timeout is automatically cleared on unmount.
 * @param callback - Function to call after the timeout
 * @param duration - Timeout duration in ms
 * @returns [start, clear]
 */
export function useClearableTimeout(callback: () => void, duration: number) {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clear = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const start = useCallback(() => {
    clear();
    timeoutRef.current = setTimeout(() => {
      callback();
      timeoutRef.current = null;
    }, duration);
  }, [callback, duration, clear]);

  useEffect(() => {
    return () => {
      clear();
    };
  }, [clear]);

  return [start, clear] as const;
}
